<template>
    <TransitionRoot as="template" :show="show">
        <Dialog as="div" class="fixed inset-0 overflow-hidden" @close="close()">
            <div class="absolute inset-0 overflow-hidden">
                <DialogOverlay class="absolute inset-0 bg-gray-500 opacity-75"/>
                <div class="fixed inset-y-0 pl-16 max-w-full right-0 flex">
                    <TransitionChild as="template" enter="transform transition ease-in-out duration-500 sm:duration-700"
                                     enter-from="translate-x-full" enter-to="translate-x-0"
                                     leave="transform transition ease-in-out duration-500 sm:duration-700"
                                     leave-from="translate-x-0" leave-to="translate-x-full">
                        <div class="w-screen max-w-3xl">
                            <form class="h-full divide-y divide-gray-200 flex flex-col bg-white shadow-xl"
                                  @submit.prevent="submit">
                                <div class="flex-1 h-0 overflow-y-auto">
                                    <div class="py-6 px-4  bg-gradient-to-b from-pitch-purple to-dark-pitch-purple sm:px-6">
                                        <div class="flex items-center justify-between">
                                            <DialogTitle class="text-lg text-white">
                                                Assign Expense To Fixture
                                            </DialogTitle>
                                            <div class="ml-3 h-7 flex items-center">
                                                <button type="button"
                                                        class="bg-dark-pitch-purple rounded-md text-indigo-200 hover:text-white focus:outline-none focus:ring-2 focus:ring-white"
                                                        @click="close()">
                                                    <span class="sr-only">Close panel</span>
                                                    <XIcon class="h-6 w-6" aria-hidden="true"/>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                    <SlideOutErrors :show="showErrors === true"/>
                                    <div class="flex-1 flex flex-col justify-between">
                                        <div class="px-4 divide-y divide-gray-200 sm:px-6">
                                            <div class="space-y-6 pt-6 pb-5">
                                                <div v-if="this.duplicate_expense" class="rounded-md bg-red-50 p-4">
                                                    <p>This expense has been highlighted as being a duplicate, please review the below information before accepting:</p>
                                                    {{ decodeEntities(this.duplicate_expense?.description?.replace(/<\/?[^>]+>/ig, " ")) }}
                                                    <p>
                                                        {{ formatCurrency(this.duplicate_expense?.amount, this.duplicate_expense?.currency.code, true) }} on  {{ moment(this.duplicate_expense?.paid_at, 'YYYY-MM-DD').format('DD MMM YYYY') }}
                                                        <span v-if="this.duplicate_expense?.fixture_id > 0"> on fixture 
                                                            <a :href="route('frontend.fixture.expenses.index', [this.duplicate_expense?.fixture_id])" class="text-blue-600 underline dark:text-blue-500 hover:no-underline" target="_blank">{{ this.duplicate_expense?.fixture?.title }}</a></span>
                                                        <span v-else> not assigned to a fixture</span>
                                                    </p>
                                                </div>
                                                <div>
                                                    <label for="fixture" class="block text-sm text-gray-900">
                                                        Fixture
                                                    </label>
                                                    <div class="mt-1">
                                                        <v-select class="h-full mt-1" :options="processedFixtures" label="displayText" :reduce="fixture => fixture.id" v-model="form.fixture" placeholder="Please Select" />
                                                    </div>
                                                </div>
                                                <div>
                                                    <label for="transaction-type" class="block text-sm text-gray-900">
                                                        Transaction Type
                                                    </label>
                                                    <div class="mt-1">
                                                        <v-select id="transaction_type" :disabled="isTransactionTypeDisabled" class="h-full mt-1" :options="transaction_types" label="name" :reduce="transaction_type => transaction_type.id" v-model="form.transaction_type" placeholder="Please Select" />
                                                    </div>
                                                </div>
                                                <div class="grid grid-cols-2 gap-4">
                                                    <div>
                                                        <label for="transaction-type" class="block text-sm text-gray-900">
                                                            Merchant
                                                        </label>
                                                        <div class="mt-1">
                                                            {{  this.merchant }}
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <label for="transaction-type" class="block text-sm text-gray-900">
                                                            Date
                                                        </label>
                                                        <div class="mt-1">
                                                            {{ moment(this.date, 'YYYY-MM-DD').format('DD MMM YYYY') }}
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <label for="transaction-type" class="block text-sm text-gray-900">
                                                            Amount
                                                        </label>
                                                        <div class="mt-1">
                                                            {{ formatCurrency(this.amount, this.currency_code, true) }}
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <label for="transaction-type" class="block text-sm text-gray-900">
                                                            GBP Amount
                                                        </label>
                                                        <div class="mt-1">
                                                            {{ formatCurrency(this.gbp_amount, "GBP", true) }}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div>
                                                    <label class="block text-sm font-medium text-gray-700">
                                                        Description
                                                    </label>
                                                    <div class="mt-1">
                                                        <quill-editor
                                                            content-type="html" 
                                                            v-model:content="form.description" 
                                                            class="min-h-[150px] block w-full bg-white border border-gray-300 shadow-sm py-2 px-3" 
                                                            theme="snow">
                                                        </quill-editor>
                                                    </div>
                                                </div>
                                                <div class="grid grid-cols-2 gap-4">
                                                    <div>
                                                        <label for="vat_type"
                                                               class="block text-sm text-gray-900 required">
                                                            VAT Types
                                                        </label>
                                                        <div class="mt-1">
                                                            <select required id="vat_type" v-model="form.vat_type"
                                                                    :class="{'border-red-500': form.errors.vat_type}"
                                                                    class="mt-1 block w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm">
                                                                <option value selected
                                                                        v-html="'Select a VAT Type'"></option>
                                                                <option value="VAT Receipt">VAT Receipt</option>
                                                                <option value="No VAT Receipt">No VAT Receipt</option>
                                                                <option value="Missing/Lost">Missing/Lost</option>
                                                            </select>
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <label for="vat_amount"
                                                               class="block text-sm text-gray-900 required">
                                                            VAT Amount
                                                        </label>
                                                        <div class="mt-1">
                                                            <input required type="number" id="vat_amount" v-model="form.vat_amount" step="0.01"
                                                                   class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                                   :class="{'border-red-500': form.errors.vat_amount}"
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                                <div class="col-span-2">
                                                    <label
                                                           class="block text-sm text-gray-900">
                                                        Attachments
                                                    </label>
                                                    <div class="mt-1">                                                        
                                                        <label class="w-full mb-4 flex flex-col items-center px-4 py-6 bg-white text-blue rounded-lg tracking-wide uppercase border border-blue cursor-pointer">
                                                            <svg class="w-8 h-8" fill="currentColor" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                                                                <path d="M16.88 9.1A4 4 0 0 1 16 17H5a5 5 0 0 1-1-9.9V7a3 3 0 0 1 4.52-2.59A4.98 4.98 0 0 1 17 8c0 .38-.04.74-.12 1.1zM11 11h3l-4-4-4 4h3v3h2v-3z" />
                                                            </svg>
                                                            <span v-if="form.file_uploads.length  === 0" class="mt-2 text-base leading-normal">Select a file</span>
                                                            <span v-else>{{ form.file_uploads.length }} file<span v-if="form.file_uploads.length > 1">s</span> selected for upload</span>
                                                            <input type='file' class="hidden" multiple @change="prepareFileUpload($event)" />
                                                        </label>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="flex-shrink-0 px-6 py-4 flex justify-end">
                                        <button type="button"
                                                class="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                                @click="close()">
                                            Close
                                        </button>
                                        <button type="submit" class=" ml-4 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm rounded-md text-white bg-pitch-purple hover:bg-dark-pitch-purple focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-gray-200">
                                            Assign
                                        </button>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>
<script>
import {ref, reactive, computed} from 'vue';
import {XIcon} from '@heroicons/vue/outline';
import {useForm, usePage} from '@inertiajs/vue3';
import SlideOutErrors from "@/Components/SlideOutErrors";
import moment from "moment/moment";
import {Dialog, DialogOverlay, DialogTitle, TransitionChild, TransitionRoot} from '@headlessui/vue';

export default {
    components: {
        XIcon,
        Dialog,
        DialogTitle,
        DialogOverlay,
        SlideOutErrors,
        TransitionRoot,
        TransitionChild,
    },
    props: ['fixtures', 'transaction_types'],
    data() {
        return {
            show: ref(false),
            fixtures: computed(() => usePage().props.fixtures),
            transaction_types: computed(() => usePage().props.transaction_types),
            id: null,
            amount: null,
            currency_code: null,
            gbp_amount: null,
            merchant: null,
            date: null,
            duplicate_expense: null,
            form: useForm({
                fixture: '',
                transaction_type: '',
                description: '',
                vat_type: '',
                vat_amount: '',
                receipts: [],
                file_uploads: [],
            })
        }
    },
    methods: {
        open() {
            this.show = true;
        },
        close() {
            this.form.reset();
            this.form.clearErrors();
            this.show = false;
        },
        submit() {
            this.form.post(route('frontend.expenses-for-review.assign_to_fixture', this.id), {
                onSuccess: page => {
                    this.show = false;
                    this.form.reset();
                }
            });
        },
        prepareFileUpload(event) {
            this.form.file_uploads = event.target.files
        },
        decodeEntities(encodedString) {
            const parser = new DOMParser();
            const dom = parser.parseFromString(`<!doctype html><body>${encodedString}`, 'text/html');
            return dom.body.textContent;
        }
    },
    watch: {
        'form.fixture': {
            deep: true,
            handler(id) {                
                if (id != null && id !== '' && id !== 0) {
                    var client = this.fixtures.find(fixture => fixture.id == id);
                    const prePaymentType = this.transaction_types.find(type => type.name === 'Pre-Payment');
                    if (prePaymentType) {
                        this.form.transaction_type = prePaymentType.id;
                        this.isTransactionTypeDisabled = true;
                    }
                } else if (id === '' || id === 0) {
                    this.form.transaction_type = '';
                    this.isTransactionTypeDisabled = false;
                }
            }
        }
    },
    computed: {
        processedFixtures() {
            let defaultOption = {
                id: '',
                displayText: 'Please Select'
            };

            // Add 'Non-Client Billable' option
            let nonClientBillableOption = {
                id: 0,
                displayText: 'Non-Client Billable'
            };

            let fixturesWithDisplayText = this.fixtures.map(fixture => {
                return {
                    ...fixture,
                    displayText: `${fixture.title} - ${fixture.reference}`
                };
            });

            return [defaultOption, nonClientBillableOption, ...fixturesWithDisplayText];
        }
    },
    created() {
        this.moment = moment
    }
}
</script>
